import React, { useEffect, useState, useContext, createContext } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro' // <-- import styles to be used
import Modal from './components/Modal'
import { Text } from './components/Fields'
import Button from './components/Button'
import Table from './components/Table'
import { produce } from 'immer'
import YesNo from './components/YesNo'
import useLocationsStore from './stores/locations'

const LocationsContext = createContext()

export default () => {

  const [locationToEdit, setLocationToEdit] = useState(false)
  const [locationToDelete, setLocationToDelete] = useState(false)
  const [saving, setSaving] = useState(false)
  const [quantity, setQuantity] = useState(0)
  const [warning, setWarning] = useState(false)

  const [
    locations,
    setLocations,
    fetchLocations,
  ] = useLocationsStore(state => [
    state.collection,
    state.setCollection,
    state.fetch,
  ])

  useEffect(() => {
    fetchLocations()
  }, [])

  const save = async () => {
    setSaving(true)
    const {id, ...params} = locationToEdit
    const method = id ? 'PUT' : 'POST'
    const url = id ? `/api/locations/${id}` : '/api/locations'
    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    })
    const data = await response.json()
    setSaving(false)
    if (!response.ok) {
      throw new Error(`There was an error saving the location.\n${JSON.stringify(data, null, 2)}`)
    }
    setLocations(produce(locations, draft => {
      const index = draft.findIndex(k => k.id == data.id)
      if (index > -1) {
        draft[index] = data
      } else {
        draft.push(data)
      }
      draft.sort((a, b) => a.title.localeCompare(b.title))
    }))
    setLocationToEdit(false)
  }

  return <LocationsContext.Provider value={{ setLocationToEdit, setLocationToDelete }}>
    <div>
      <div className="flex justify-between gap-4 mb-4 items-end">
        <div className='flex gap-4'>
          <h1 className="text-4xl">Locations</h1>
        </div>
        <div className="flex justify-center gap-4">
          <Button type="success" icon="plus" onClick={() => setLocationToEdit({})}>
            <FontAwesomeIcon icon={icon({name: 'plus'})} />
            New Location
          </Button>
        </div>
      </div>
      <div>
        <Table>
          <Table.Head className="sticky top-0">
            <Table.Row>
              <Table.Th>Name</Table.Th>
              <Table.Th>Notification</Table.Th>
              <Table.Th>Ops Notification</Table.Th>
              <Table.Th>SKUs</Table.Th>
              <Table.Th>Default?</Table.Th>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {locations.map((location) => (
              <Row location={location} key={location.id} />
            ))}
          </Table.Body>
        </Table>
      </div>

      <Modal isOpen={locationToDelete} onClose={() => setLocationToDelete(false)}>
        <Modal.Header onClose={() => setLocationToDelete(false)}>
          You Sure?
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this location?</p>
          <p>This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex items-center justify-between gap-4">
            <a href="#" onClick={() => setLocationToDelete(false)}>
              Cancel
            </a>
            <Button type="danger" onClick={async () => {
              const response = await fetch(`/api/locations/${locationToDelete.id}`, {
                method: 'DELETE',
              })
              if (!response.ok) {
                throw new Error('There was an error deleting the location.')
              }
              setLocations(locations.filter(location => location.id != locationToDelete.id))
              setLocationToDelete(false)
            }}>
              <FontAwesomeIcon icon={icon({name: 'trash'})} />
              Delete
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal isOpen={locationToEdit} onClose={() => setLocationToEdit(false)}>
        <Modal.Header onClose={() => setLocationToEdit(false)}>
          Editing {locationToEdit.title ? locationToEdit.title : 'New Location'}
        </Modal.Header>
        <Modal.Body>
          <Text
            value={locationToEdit.title ?? ''}
            onChange={(e) => setLocationToEdit({...locationToEdit, title: e.target.value})}
            label="Location Name"
            className="mb-4"
          />
          <Text
            value={locationToEdit.notification ?? ''}
            onChange={(e) => setLocationToEdit({...locationToEdit, notification: e.target.value})}
            label="Notification"
            className="mb-4"
          />
          <Text
            value={locationToEdit.ops_notification ?? ''}
            onChange={(e) => setLocationToEdit({...locationToEdit, ops_notification: e.target.value})}
            label="OPS Notification"
            className="mb-4"
          />
        </Modal.Body>
        <Modal.Footer>
          <div className="flex items-center justify-between gap-4">
            <a href="#" onClick={() => setLocationToEdit(false)}>
              Cancel
            </a>
            <Button type="success" onClick={save} disabled={saving}>
              <FontAwesomeIcon icon={icon({name: 'save'})} />
              Save
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  </LocationsContext.Provider>
}

const Row = ({location}) => {

  const { setLocationToEdit, setLocationToDelete, setLocationToShip } = useContext(LocationsContext)
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    const closeMenu = (e) => {
      if (!e.target.closest('.relative')) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('click', closeMenu)
    return () => {
      document.removeEventListener('click', closeMenu)
    }
  }, [])

  const handleDelete = () => {
    setMenuOpen(false)
    setLocationToDelete(location)
  }

  const handleEdit = () => {
    setMenuOpen(false)
    setLocationToEdit(location)
  }

  return <Table.Row>
    <Table.Td className="flex items-center gap-2">
      <div className="relative">
        <button onClick={() => setMenuOpen(old => !old)} className="p-2"><FontAwesomeIcon icon={icon({name: 'ellipsis-v'})} /></button>
        {menuOpen && <div className="absolute left-0 top-full bg-white shadow-md rounded-md z-10">
          <button className="flex items-center gap-2 p-2" onClick={handleEdit}>
            <FontAwesomeIcon icon={icon({name: 'edit'})} />
            Edit
          </button>
          <button className="flex items-center gap-2 text-red-500 p-2" onClick={handleDelete}>
            <FontAwesomeIcon icon={icon({name: 'trash'})} />
            Delete
          </button>
        </div>}
      </div>
      {location.title}
    </Table.Td>
    <Table.Td>{location.notification}</Table.Td>
    <Table.Td>{location.ops_notification}</Table.Td>
    <Table.Td>{location.product_count}</Table.Td>
    <Table.Td><YesNo yesClasses="font-bold text-green-600" value={location.default} /></Table.Td>
  </Table.Row>
}

